import React, { ChangeEvent, ChangeEventHandler, KeyboardEventHandler, MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import sengiCoin from "../../../assets/sengi_coin.png"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas, faChevronRight, faCopy, faMicrophoneLines, faPaperclip, faCaretLeft, faHeadphones, faImage, faFolderOpen, faCamera, faMicrophoneLinesSlash, faScrewdriverWrench, faGears, faRotateLeft, faStop } from '@fortawesome/free-solid-svg-icons'
import { useChatHistory } from '../../../hooks/ChatHooks';
// import { ApiServicesLLMs } from "../../apis/ApiServicesLLMs";
import { ChatMessage, ChatDataProps } from '../../../types/backend/SengiLabsTypes';
import { ChatComponent } from "./GenericChatComponent";
// import { PipelineFlow } from "../../components/AiPipelineFlow/PipelineFlow"
import { FlowBusinessSummary, FlowGPTProxy, FlowImageGenerator, FlowLargeLanguageModel, FlowMailToObject, FlowOCR, FlowPresentationGenerator, FlowSecureGPT, FlowSpeechRecognition, FlowStickerGenerator, FlowTextToSpeech, FlowTranslator } from "../../../pages/PageStructure/data";
import { PipelineData } from '../../AiPipelineFlow/PipelineTypes';
import { ApiPortalBackend, PortalConversation } from '../../../apis/ApiPortalBackend';
import { useTranslation } from 'react-i18next';
import { SimpleToast } from '../../../components/ui/SimpleToast';
import { AiDisclosureNotice } from '../../../components/ui/AiDisclosureNotice';
import { useBootstrapTheme } from '../../../contexts/BootstrapThemeContext';
import { Config } from '../../../config/Config';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { useTrackedRun } from '../../../hooks/useTrackedRun';
import { isFeatureEnabled } from '../../../features';
import { detectSpeechLang, toSpeechLang } from '../../../utils/chat/speechLanguage';
import { parseResponseToChatDataProps } from '../../../utils/chat/chatPropsParsing';
import { stateIconError, stateIconWaiting } from '../../../types/ai/states';
import { fileToDataUrl } from '../../../utils/images/imageConvertFormats';
import { SengiColors } from '../../../utils/SengiColors';
import { ChatProps, ChatResponseState, PendingChatFile } from '../../../types/chat';
import { abortChatRun, clearChatAbort, getChatRuntimeState, markChatAsRead, registerChatAbort, updateChatRuntimeState, useChatRuntimeState } from '../../../utils/chat/runtimeChat';
import { estimateTokensFromChars, streamChatCompletion, StreamSetupError } from '../../../utils/chat/streamChat';
import { StageBadge } from '../../ui/StageBadge';

// Masterplan Paket 3: Kostenvorschau nur noch auf Dev (creditsUi) - sonst weder Anzeige noch Estimate-Request.
const SHOW_COST_ESTIMATE = isFeatureEnabled('creditsUi');
// Masterplan Paket 4 Punkt 8: die Chat-API ist (noch) nicht multimodal - Anhang-UI inkl. Drag&Drop nur
// auf Dev/Stage zum Weiterentwickeln, Prod blendet sie aus (chatAttachments).
const SHOW_ATTACHMENTS = isFeatureEnabled('chatAttachments');
// Eingabefeld waechst mit dem Text bis zu dieser Hoehe, danach scrollt es (Paket 4 Punkt 3).
const CHAT_INPUT_MAX_HEIGHT_PX = 200;

// Das Element, das die Nachrichtenliste tatsaechlich scrollt (die Liste selbst oder ihr
// overflow-auto-Wrapper) - nie ein Container ausserhalb des Chats.
const getMessageScroller = (container: HTMLElement): HTMLElement | null =>
  [container, container.parentElement].find(
    (el): el is HTMLElement => !!el && el.scrollHeight > el.clientHeight
  ) ?? null;

library.add(fas, faChevronRight)

export default function Chat({
  chatID,
  getChatMessages,
  defaultChatTitle,
  onConversationCreated
}:ChatProps) {
  const { t, i18n } = useTranslation(['chat', 'common']);
  const context = useAppContext();
  const { effectiveTheme } = useBootstrapTheme();
  const [pipelineFlowData, setPipelineFlowData] = useState<PipelineData>(FlowGPTProxy);
  const [pipelineType, setPipelineType] = useState<string>("basis");
  const [pipelineName, setPipelineName] = useState<string>("new");
  const baseUrl = Config.json.AiServiceProxyUrl;
  
  const [thinkingText, setThinkingText] = useState("");
  const { startRun, completeRun, failRun, insufficientCreditsDialog } = useTrackedRun();
  const thinkingTexts = useMemo( () => [
      t("chat:thinking.analyzing"),
      t("chat:thinking.considering"),
      t("chat:thinking.formulating"),
      t("chat:thinking.reviewing"),
      t("chat:thinking.connecting")
    ], [t]
  );

  const [chatMessages, addMessage, addMessages, getLastMessage,reloadMessages, appendLocalMessage] = useChatHistory(chatID, {defaultTitle: defaultChatTitle, onConversationCreated});
  const runtimeState = useChatRuntimeState(chatID);
  const [chatInputString, setChatInputString] = useState<string>("");
  const [pendingFiles, setPendingFiles] = useState<PendingChatFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  // Obergrenz-Annahme fuer die "bis zu X"-Vorschau: der einfache Chat-Request (siehe body in
  // sendMessage) setzt kein max_tokens/num_predict, wir preisen also den worst case mit dieser
  // angenommenen Ausgabe-Obergrenze. MUSS mit der echten Ausgabe-Deckelung des Backends
  // (gpt-oss:20b via cProxy) abgeglichen werden - siehe TASKS.md Kosten-Audit (2026-07-22).
  const CHAT_ASSUMED_MAX_OUTPUT_TOKENS = 1000;
  const [estimatedCost, setEstimatedCost] = useState<number|null>(0);
  const [estimatedIsUpperBound, setEstimatedIsUpperBound] = useState<boolean>(true);

  //speech recognition
  const [recognizing, setRecognizing] = useState(false);
  // Diktier-/Vorlesesprache folgt der UI-Sprache (vorher fix 'en-US' - deutsche Antworten wurden
  // mit englischer Stimme vorgelesen, Masterplan Paket 4 Punkt 6).
  const language = toSpeechLang(i18n.resolvedLanguage);
  // Welche Nachricht gerade vorgelesen wird (null = keine) - fuer den Stop-Button (Paket 4 Punkt 7).
  const [speakingId, setSpeakingId] = useState<string | null>(null);
  const [recognition, setRecognition] = useState<any |null>(null);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const speechInputBaseRef = useRef("");

  //for taking pictures
  const [takenPicture, setTakenPicture] = useState<string | null>(null);

  //for adjusting textmessage
  const [accodionInput, setAccordionInput] = useState({secureGPT: false, aiService:''});
  const [showAccordion, setShowAccordion] = useState(false);
  const [inputSectionSpacing, setInputSectionSpacing] = useState(170);
  const [aiServiceList, setAiServiceList] = useState<string[]>([]);
  const [foundService, setFoundService] = useState(true);

  useEffect(() => {
    document.title = 'Chat';
    let navbarTitle = document.getElementById('navbar_title');
    if(navbarTitle){
        navbarTitle.textContent = `Chat - ${pipelineName}`;
    }
    calculateCost('');
    if (window.SpeechRecognition || window.webkitSpeechRecognition){
      //only supported by google chrome and microsoft edge
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      setRecognition(new SpeechRecognition()); 
    }
    loadAiServiceList();

    // clear on unmount
    return () => {
      if(timeoutChangedHandlerRef.current) clearTimeout(timeoutChangedHandlerRef.current);
    }
  },[])

  useEffect(() => {
    let navbarTitle = document.getElementById('navbar_title');
    if(navbarTitle){
        navbarTitle.textContent = `Chat - ${pipelineName}`;
    }
  }, [pipelineName]);

  useEffect(() => {
    let splitData = context.selectedPipeline.split("/");
    if(splitData.length > 1)
      setPipelineName(splitData[1]);

    switch(splitData[0]) {
      case "b":
        setPipelineType("basis");
        break;
      case "e":
        setPipelineType("extended");
        break;
      case "u":
        setPipelineType("user");
        break;
      case "c":
        setPipelineType("chat");
        break;
    }
    if(splitData[0] == "b" || splitData[0] == "e") {

      switch(splitData[1]) {
        case "GPTProxy":
          setPipelineFlowData(FlowGPTProxy);
          break;
        case "SecureGPT":
          setPipelineFlowData(FlowSecureGPT);
          break;

        case "LargeLanguageModel":
          setPipelineFlowData(FlowLargeLanguageModel);
          break;
        case "ImageGenerator":
          setPipelineFlowData(FlowImageGenerator);
          break;
        case "TextToSpeech":
          setPipelineFlowData(FlowTextToSpeech);
          break;
        case "SpeechRecognition":
          setPipelineFlowData(FlowSpeechRecognition);
          break;
        case "OCR":
          setPipelineFlowData(FlowOCR);
          break;
        case "Translator":
          setPipelineFlowData(FlowTranslator);
          break;
        case "MailToObject":
          setPipelineFlowData(FlowMailToObject);
          break;
        case "BusinessSummary":
          setPipelineFlowData(FlowBusinessSummary);
          break;
        case "PresentationGenerator":
          setPipelineFlowData(FlowPresentationGenerator);
          break;
        case "StickerGenerator":
          setPipelineFlowData(FlowStickerGenerator);
          break;
      }
    }

    if(splitData[0] == "u" ) {
      // User-Pipeline abrufen
    }
    
    if(splitData[0] == "c" ) {
      // Chat-Pipeline abrufen
    }
  }, [context.selectedPipeline]);

  useEffect(() => {
    if (!recognition) return;

    recognition.lang = language;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event: any) => {
        const transcript = Array.from(event.results).map((result: any) => result[0]?.transcript ?? "").join("");
        const base = speechInputBaseRef.current;
        setChatInputString(base ? `${base} ${transcript}` : transcript);
    };
    recognition.onstart = () => {
        setSpeechError(null);
        setRecognizing(true);
    };
    recognition.onend = () => {
        setRecognizing(false);
    };
    recognition.onerror = ( event: any ) => {
        setRecognizing(false);
        setSpeechError(event?.error ?? "speech-recognition-error");
        console.warn("Speech recognition error:", event);
    };
    return () => {
        recognition.onresult = null;
        recognition.onstart = null;
        recognition.onend = null;
        recognition.onerror = null;
        try {
            recognition.abort();
        } catch {
            // already stopped
        }
    };
}, [recognition, language]);

  // Autosize: laeuft auf jede Aenderung (Tippen, Diktat, Leeren nach dem Senden).
  useEffect(() => {
    const el = chatInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, CHAT_INPUT_MAX_HEIGHT_PX)}px`;
    el.style.overflowY = el.scrollHeight > CHAT_INPUT_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, [chatInputString]);

  // FUNCTIONS
  const startRecognition = async() => {
    if (!recognition || recognizing) return;
    setSpeechError(null);
    try {
        if (navigator.mediaDevices?.getUserMedia) {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            stream.getTracks().forEach(track => track.stop());
        }
        speechInputBaseRef.current = chatInputString.trimEnd();
        recognition.start();
    } catch (error) {
        console.error("Unable to start speech recognition:", error);
        setSpeechError(error instanceof Error ? error.message : "microphone-unavailable");
        setRecognizing(false);
    }
  };
  const stopRecognition = () => {
    if (!recognition || !recognizing) return;
    try {
        recognition.stop();
    } catch (error) {
        console.warn("Unable to stop speech recognition:", error);
        setRecognizing(false);
    }
  };

  const toggleRecognition = () => {
    if (recognizing) {
        stopRecognition();
    } else {
        void startRecognition();
    }
  };
  const handleImageCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const reader = new FileReader();

      // Lade die Bilddatei und speichere sie als Daten-URL
      reader.onloadend = () => {
        setTakenPicture(reader.result as string); // Daten-URL speichern
      };
      reader.readAsDataURL(file); // Bilddatei als Base64-Daten-URL lesen
    }
  };
  const onClickButtonAttachFiles = (id:string) => {
    const fileUpload = document.getElementById(id);
    if(fileUpload){
      fileUpload.click();
    }
  };
  const deleteAttachedFile = (index: number) => {
      setPendingFiles(prev => {
          const attachment = prev[index];
          if (attachment) {
              URL.revokeObjectURL(attachment.previewUrl);
          }
          return prev.filter((_, i) => i !== index);
      });
  };
  const readAloudText = (text: string, messageId: string) => {
    if (!('speechSynthesis' in window)) return;
    // Zweiter Klick auf dieselbe Nachricht stoppt das Vorlesen.
    if (speakingId === messageId) {
      speechSynthesis.cancel();
      setSpeakingId(null);
      return;
    }
    speechSynthesis.cancel();
    const reading = new SpeechSynthesisUtterance(text);
    reading.lang = detectSpeechLang(text, language);
    const finish = () => setSpeakingId(current => (current === messageId ? null : current));
    reading.onend = finish;
    reading.onerror = finish;
    setSpeakingId(messageId);
    speechSynthesis.speak(reading);
  };

  // Vorlesen endet beim Chat-Wechsel/Verlassen der Seite, statt im Hintergrund weiterzulaufen.
  useEffect(() => () => {
    if ('speechSynthesis' in window) speechSynthesis.cancel();
    setSpeakingId(null);
  }, [chatID]);

  const getMessageText = (contents: ChatDataProps[]) =>
    contents
        .filter(item => item.type === "text")
        .sort((a, b) => a.position - b.position)
        .map(item => item.element)
        .join("\n");

  const showCopiedToast = () => {
      const copiedToast =  document.getElementById("CopiedTextSuccesfull");
      if (!copiedToast) return;

      copiedToast.style.display = "block";
      window.setTimeout(() => {copiedToast.style.display = "none";}, 1000);
  };

  const copyMessage = async (contents: ChatDataProps[]) => {
      const text = getMessageText(contents);
      if (!text) return;

      await navigator.clipboard.writeText(text);
      showCopiedToast();
  };
  

  //SETTINGS
  const loadAiServiceList = () =>{
    //load list of services
    setAiServiceList(['gpt-4-pro','gpt-4-mini','dolly']);
    setAccordionInput(inputs =>({...inputs,['aiService']:'gpt-4-pro'}));
  };
  const handleChangeAccordionCheckboxInput = (event:React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.checked;
    setAccordionInput(input => ({ ...input, [name]: value }));
  };
  const handleChangeAiServiceInput = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const name = event.target.name;

    setAccordionInput(input => ({ ...input, [name]: value }));
    let aiService = aiServiceList.find(x => x === value);
    if(aiService){
      setFoundService(true);
    }
    else{
      setFoundService(false);
    }
  };

  //HANDLE CHAT INPUT
  // "bis zu X"-Vorschau: wir senden die rohe Prompt-Zeichenzahl + die angenommene Ausgabe-Obergrenze
  // an SL-CostsService (via /v1/runs/estimate). Der Service macht die chars->tokens-Umrechnung selbst
  // (kein lokales Zaehlen im Frontend - so nutzen Vorschau und der echte Abzug bei completeRun
  // denselben Tokenizer) und liefert estimateKind: "upper_bound" zurueck. Der reale Abzug erfolgt
  // spaeter aus den echten Tokens, ist praktisch immer kleiner als diese Obergrenze.
  const calculateCost = async (value:string) => {
    if (!SHOW_COST_ESTIMATE) return;
    const estimate = await ApiPortalBackend.EstimateRunCost({
      type: 'llm',
      inputChars: value.length,
      maxOutputTokens: CHAT_ASSUMED_MAX_OUTPUT_TOKENS,
    });
    if (estimate && estimate.estimated) {
      setEstimatedCost(estimate.credits);
      setEstimatedIsUpperBound(estimate.estimateKind === 'upper_bound');
    } else {
      setEstimatedCost(null);
    }
  }

  const timeoutChangedHandlerRef = useRef<NodeJS.Timeout | null>(null);
  const onChatInputChangedHandler : ChangeEventHandler<HTMLTextAreaElement> = (event : ChangeEvent<HTMLTextAreaElement>) => {
    // calculate Cost here
    const value = event.target.value;
    setChatInputString(value);
    
    // Kostenvorschau entprellt neu berechnen. Der alte useCallback-im-Handler war ein
    // React-Hook-Fehlgebrauch (Hook in einem Event-Handler, Rueckgabe verworfen) und feuerte nie -
    // dadurch aktualisierte sich die Vorschau beim Tippen ueberhaupt nicht.
    if (timeoutChangedHandlerRef.current) clearTimeout(timeoutChangedHandlerRef.current);
    timeoutChangedHandlerRef.current = setTimeout(() => {
      calculateCost(value);
      timeoutChangedHandlerRef.current = null;
    }, 400);
  };

  const onChatInputKeyDownHandler = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) { // `!e.shiftKey` ermöglicht Shift+Enter für Zeilenumbruch (falls du ein <textarea> nutzt)
      event.preventDefault();                   // verhindert das "Absenden" als Form‑Submit oder Page‑Reload
      sendMessage();
    }
  };
  const addFiles = async (files: File[]) => {
    const supported = files.filter(file => file.type.startsWith("image/") || file.type === "application/pdf");
    if (supported.length === 0) return;

    const attachments: PendingChatFile[] =
        await Promise.all(
            supported.map(async file => ({
                file,
                type: file.type === "application/pdf" ? "pdf" as const : "image" as const,
                previewUrl: URL.createObjectURL(file),
                content: await fileToDataUrl(file)
            }))
        );

    setPendingFiles(prev => [
        ...prev,
        ...attachments
    ]);
};
  const addMessageToChat = async(role:string,content: ChatDataProps[],inputFiles:ChatDataProps[],id:string): Promise<{message: ChatMessage; conversationId: string | null;}> => {
    const newMessage: ChatMessage = {
        id,
        role,
        contents: [
            ...content,
            ...inputFiles
        ],
        createdAt: new Date(),
        updatedAt: new Date()
    };

    const conversationId = await addMessage(newMessage);
    return { message: newMessage, conversationId};
  }
  const sendMessage = async() => {
    if (chatID && getChatRuntimeState(chatID).state === "thinking") return;

    //avoid sending empty messages
    const text = chatInputString.trim();
    const hasFiles = pendingFiles.length > 0;

    if (!text && !hasFiles) return;

    const contents: ChatDataProps[] = [];
    if (text) {
        contents.push({
            type: 'text',
            element: text,
            position: 0
        });
    }

    const positionOffset =contents.length;
    const fileContents: ChatDataProps[] = pendingFiles.map((attachment, index) => ({type: attachment.type, element: attachment.content, position: positionOffset + index}));
    
    // create new user message and store it in local session -> avoid calling ai every time after opening chat if user is last writer
    const {message: newMessage,conversationId} = await addMessageToChat("user",contents,fileContents,"");
    if (!conversationId) return;
    const messagesForAi: ChatMessage[] = [...chatMessages,newMessage];
    void callAiModel(conversationId, messagesForAi);
    pendingFiles.forEach(attachment => {URL.revokeObjectURL(attachment.previewUrl);});
    setChatInputString("");
    setPendingFiles([]);
  }
  const onClickChatInputSendHandler : React.MouseEventHandler<HTMLDivElement> = (event : MouseEvent<HTMLDivElement>) => {
    sendMessage();
  };
  const retryLastUserMessage = () => {
    if (!chatID) return;
    const runtime = getChatRuntimeState(chatID);
    if (runtime.state === "thinking") return;

    let lastUserIndex = -1;
    for (let i = chatMessages.length - 1;i >= 0;i--) {
        if (chatMessages[i].role === "user") {
            lastUserIndex = i;
            break;
        }
    }

    if (lastUserIndex < 0) return;
    /*
     * Verlauf nur bis einschließlich
     * der letzten User-Nachricht.
     *
     * Es wird KEINE neue User-Nachricht
     * persistiert.
     */
    const messagesForRetry = chatMessages.slice(0, lastUserIndex + 1);
    void callAiModel(chatID,messagesForRetry);
  };

  // Antwort als Stream (cProxy POST /chat/stream, Masterplan Paket 4b). Der Teiltext liegt im
  // Runtime-State der Conversation (laeuft beim Chat-Wechsel weiter). Faellt auf den bisherigen
  // /chat-Pfad zurueck, wenn der Stream gar nicht erst aufgebaut werden kann.
  const callAiModel = async (conversationId: string,messages: ChatMessage[]) => {
    updateChatRuntimeState(conversationId,{state: "thinking",error: null,streamingText: null});
    const bodyMessages = messages.map(chat => {
        const textContent = chat.contents
            .filter(item => item.type === "text")
            .sort((a, b) => a.position - b.position)
            .map(item => item.element)
            .join("\n");
        return {
            role: chat.role.toLowerCase(),
            content: textContent
        };
    });
    //send messsage to ai
    const body = {
        model: "gpt-oss:20b",
        messages: bodyMessages
      };

    const runHandle = await startRun('llm', { estimatedCost: 0 });
    if (!runHandle) {
      // Either insufficient balance (dialog shown by useTrackedRun) or another failure - fail
      // closed, don't call cProxy without a tracked run.
      updateChatRuntimeState(conversationId,{state: "error",error:t("chat:error.requestFailed")});
      return;
    }

    // Antwort persistieren (inkl. codeClass - sonst verliert Code nach dem Reload sein Highlighting)
    // und sofort lokal anzeigen, damit sie zwischen Stream-Ende und Reload nicht kurz verschwindet.
    const persistAssistantReply = async (reply: string) => {
      const parsedReply: ChatDataProps[] = parseResponseToChatDataProps(reply);
      const sortedReply = [...parsedReply].sort((a, b) =>a.position - b.position);
      const content = sortedReply.map(item => item.element).join("\n");
      const contentParts = sortedReply.map(item => ({type: item.type, content: item.element, codeClass: item.codeClass}));
      await ApiPortalBackend.PostConversationMessage(conversationId, "assistant", content, contentParts);
      appendLocalMessage(conversationId, { id: "", role: "assistant", contents: sortedReply, createdAt: new Date(), updatedAt: new Date() });
      updateChatRuntimeState(conversationId,{state: null,error: null,streamingText: null,unread: true,messageRevision: Date.now()});
    };

    // Bisheriger, nicht-streamender Pfad - nur noch Fallback.
    const callNonStreaming = async () => {
      const res = await fetchWithAuth(`${baseUrl}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const errorText = await res.text();
        failRun(runHandle,`Chat request failed (HTTP ${res.status})`);
        const shownError = res.status === 403 ? t("common:modelUnavailable.body") : (errorText || t("chat:error.requestFailed"));
        updateChatRuntimeState(conversationId,{state: "error", error: shownError, streamingText: null});
        return;
      }
      const data = await res.json();
      await persistAssistantReply(data.reply || t("chat:error.emptyResponse"));
      const usage = data.raw?.usage;
      completeRun(runHandle, {
        inputTokens: usage?.prompt_tokens ?? 0,
        outputTokens: usage?.completion_tokens ?? 0,
      });
    };

    const controller = new AbortController();
    registerChatAbort(conversationId, controller);
    // Teiltext-Updates gedrosselt, damit nicht jedes Token einen Re-Render aller Chat-Abonnenten ausloest.
    let pendingText: string | null = null;
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      flushTimer = null;
      if (pendingText !== null) updateChatRuntimeState(conversationId,{streamingText: pendingText});
    };

    try {
      // Ohne "model": /chat/stream reicht den Namen roh an vLLM durch, und der Legacy-Name
      // "gpt-oss:20b" (den /chat intern uebersetzt) existiert dort nicht (404 NotFoundError, live
      // gesehen 2026-09-21). Fehlt model, nimmt cProxy sein Standard-Chat-Modell (SL_cProxy/API.md).
      const streamBody = { messages: bodyMessages };
      const result = await streamChatCompletion(`${baseUrl}/chat/stream`, streamBody, {
        signal: controller.signal,
        onDelta: text => {
          pendingText = text;
          if (!flushTimer) flushTimer = setTimeout(flush, 60);
        },
      });
      if (flushTimer) clearTimeout(flushTimer);

      const inputChars = bodyMessages.reduce((sum, m) => sum + m.content.length, 0);
      // Echte Token aus dem Usage-Chunk; fehlt er (Abbruch/Verbindungsabriss), grob schaetzen statt
      // mit 0 abzurechnen (cProxy-Vertrag: fehlende Messung nie als 0 behandeln).
      const tokens = result.usage
        ? { inputTokens: result.usage.prompt_tokens, outputTokens: result.usage.completion_tokens }
        : { inputTokens: estimateTokensFromChars(inputChars), outputTokens: estimateTokensFromChars(result.text.length) };

      if (!result.text.trim()) {
        if (result.aborted) {
          failRun(runHandle, "Chat stream aborted by user before any output");
          updateChatRuntimeState(conversationId,{state: null,error: null,streamingText: null});
        } else {
          failRun(runHandle, "Chat stream ended without output");
          updateChatRuntimeState(conversationId,{state: "error",error: t("chat:error.emptyResponse"),streamingText: null});
        }
        return;
      }

      // Auch eine abgebrochene/abgerissene Antwort wird gespeichert - der Nutzer hat sie ja gesehen.
      await persistAssistantReply(result.text);
      completeRun(runHandle, tokens);
    } catch (err:any) {
        if (flushTimer) clearTimeout(flushTimer);
        if (err instanceof StreamSetupError && err.status === 403) {
          // Modell in dieser Umgebung gesperrt (cProxy-Modell-Policy) - kein Fallback (der /chat-Pfad
          // waere genauso gesperrt), verstaendlicher Text statt Roh-JSON.
          failRun(runHandle, "Model not available in this environment (HTTP 403)");
          updateChatRuntimeState(conversationId,{state: "error", error: t("common:modelUnavailable.body"), streamingText: null});
          return;
        }
        if (err instanceof StreamSetupError && err.status !== 401 && err.status !== 422) {
          // Stream liess sich nicht aufbauen (Endpoint fehlt, 5xx, Netzwerk) -> alter Pfad, gleicher Run.
          console.warn("Chat stream unavailable, falling back to /chat:", err.message);
          try {
            await callNonStreaming();
          } catch (fallbackErr) {
            const message = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
            failRun(runHandle, `Chat request error: ${message}`);
            updateChatRuntimeState(conversationId,{state: "error", error: message || t("chat:error.connectionError"), streamingText: null});
          }
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        failRun(runHandle, `Chat request error: ${message}`);
        updateChatRuntimeState(conversationId,{state: "error", error: message ||t("chat:error.connectionError"), streamingText: null});
    } finally {
        clearChatAbort(conversationId, controller);
    }
  };
  const chooseRandom = () => {
    setThinkingText(prev => {
        if (thinkingTexts.length <= 1) return thinkingTexts[0] ?? "";
      
        const options =thinkingTexts.filter(item => item !== prev);
        return options[Math.floor(Math.random() * options.length)];
    });
  };

  useEffect(() => {
    if (chatMessages.length > 0 && getChatMessages) {
      getChatMessages([...chatMessages]);
    }
  }, [chatMessages, getChatMessages]);
  useEffect(() => {
    if (runtimeState.state !== "thinking") return;
    chooseRandom();
    const interval = window.setInterval(chooseRandom, 3000);

    return () => window.clearInterval(interval);
  }, [runtimeState.state, thinkingTexts]);
  useEffect(() => {
    if (!chatID ||runtimeState.messageRevision === 0) return;

    void reloadMessages();
  }, [chatID, runtimeState.messageRevision]);
  useEffect(() => {
    if (!chatID) return;
    markChatAsRead(chatID);
  }, [chatID, runtimeState.unread]);

  /* -------------- 2️⃣ Auto‑Scroll bei Änderung des Chat‑Arrays -------------- */
  const chatMessagesContainerRef = useRef<HTMLDivElement | null>(null);

  // Gestreamte Teilantwort (Paket 4b) - einmal pro Update geparst, nicht pro Render.
  const streamingContent = useMemo(
    () => runtimeState.streamingText ? parseResponseToChatDataProps(runtimeState.streamingText) : [],
    [runtimeState.streamingText]
  );

  // Waehrend des Streamings nur mitscrollen, wenn der Nutzer unten ist - wer hochgescrollt hat, um
  // etwas nachzulesen, wird nicht zurueckgerissen.
  const stickToBottomRef = useRef(true);
  const onMessagesScroll = (event: React.UIEvent<HTMLElement>) => {
    const el = event.target as HTMLElement;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => {
    if (!runtimeState.streamingText || !stickToBottomRef.current) return;
    const container = chatMessagesContainerRef.current;
    const scroller = container ? getMessageScroller(container) : null;
    if (scroller) scroller.scrollTop = scroller.scrollHeight;
  }, [runtimeState.streamingText]);

  useEffect(() => {
    // Sicherstellen, dass das Element existiert
    const container = chatMessagesContainerRef.current;
    if (!container) return;
    
    // Gibt es überhaupt ein Kind?  Falls nicht, abbrechen.
    const lastChild = container.lastElementChild;
    if (!lastChild) return;

    // Nur die Nachrichtenliste selbst scrollen - KEIN scrollIntoView: das scrollt alle Vorfahren
    // mit, auch overflow:hidden-Container des Plattform-Rahmens, und schob so die ganze Ansicht hoch
    // (Kopfzeile weg, grauer Streifen unten; Masterplan Paket 4 Punkte 1/2/4).
    const scroller = getMessageScroller(container);
    if (!scroller) return;
    const top = lastChild.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTo({ top, behavior: 'smooth' });
  }, [chatMessages]); // <-- Auslöser: neue/aktualisierte Nachrichten

  return (
    <>
      
      <div className={`d-flex flex-column h-100 p-2 position-relative`}
        style={{
            transition: 'margin-left 0.3s ease-in-out',
        }}
        onDragEnter={e => {
          e.preventDefault();
          e.stopPropagation();

          if ( SHOW_ATTACHMENTS && e.dataTransfer.types.includes( "Files"  ) ) {
              setDragActive(true);
          }
        }}
        onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
        }}
        onDragLeave={e => {
          e.preventDefault();
          e.stopPropagation();
          const related = e.relatedTarget as Node | null;
          if (!related || !e.currentTarget.contains(related)) {
              setDragActive(false);
          }
        }}
        onDrop={e => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
            if (SHOW_ATTACHMENTS) addFiles(Array.from(e.dataTransfer.files));
        }}
      >
        {insufficientCreditsDialog}
        {dragActive && (
          <div
              className="position-absolute inset-0 d-flex align-items-center justify-content-center rounded border border-2 border-purple"
              style={{
                  inset: 0,
                  zIndex: 100,
                  pointerEvents: "none",
                  background: "rgba(147,129,255,0.08)",
                  backdropFilter: "blur(1px)"
              }}
          >
              <div className="h3 text-secondary">
                  <FontAwesomeIcon
                      icon={faPaperclip}
                      className="me-2"
                  />
                  {t("chat:dropFiles")}
              </div>
          </div>
      )}
        <div className="row">
          <div aria-live="polite" aria-atomic="true" className="position-relative mt-2" style={{zIndex:500, paddingLeft:'45%'}}>
            <SimpleToast
                id='CopiedTextSuccesfull'
                header={false}
                bodyText='Copied!'
                bodyClass={`fw-bold ${ effectiveTheme === 'dark'? ' text-dark': 'text-light'}`}
                width='fit-content'
                classname={`position-absolute top-0 text-center ${ effectiveTheme === 'dark'? ' bg-secondary': 'bg-primary'}`}
            />
          </div>
        </div>
        <div id="" className="flex-grow-1 overflow-auto" onScrollCapture={onMessagesScroll}>
          <div id="chatMessages" className="chat-area scroll-y" ref={chatMessagesContainerRef} >
            {chatMessages.map((entry : ChatMessage, index : number) => {
              if(entry.role == "user") {
                let chatContent: ChatDataProps[]= [...entry.contents];
                //sort by defined position from 0 to higher
                chatContent.sort((a,b) => a.position - b.position);
                return (
                  <React.Fragment key={entry.id || `msg-${index}`}>
                    <div className="message-right">
                      <ChatComponent
                          items={chatContent}
                          id={String(index)}
                          componentClass="message-right-content"
                          pdfHeight="auto"
                          pdfWidth="auto"
                      />
                    </div>
                    {getMessageText(chatContent) && (
                      <div className="d-flex justify-content-end text-secondary pe-2 mb-2 gap-1">
                          <button
                              type="button"
                              className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                              title={speakingId === `user-${index}` ? t("chat:title.stopReading") : t("chat:title.readAloud")}
                              onClick={() => readAloudText(getMessageText(chatContent), `user-${index}`) }
                          >
                              <FontAwesomeIcon
                                  icon={speakingId === `user-${index}` ? faStop : faHeadphones}
                                  size="1x"
                              />
                          </button>
                          <button
                              type="button"
                              className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                              title={t("chat:title.copy")}
                              onClick={() => void copyMessage(chatContent)}
                          >
                              <FontAwesomeIcon
                                  icon={faCopy}
                                  size="1x"
                              />
                          </button>
                      </div>
                    )}
                  </React.Fragment>
                )
              }
              else {
                let chatContent: ChatDataProps[]=[...entry.contents];
                //sort by defined position from 0 to higher
                chatContent.sort((a,b) => a.position - b.position);
                return (
                  <div className="message-left" key={entry.id || `msg-${index}`}>
                    <ChatComponent
                      items={chatContent}
                      id ={String(index)}
                    />
                    <div className="float-start text-secondary pe-2 mb-2">
                      <button
                        type="button" 
                        className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                        data-bs-toggle="tooltip" 
                        data-bs-placement="top" 
                        title={speakingId === `ai-${index}` ? t('chat:title.stopReading') : t('chat:title.readAloud')}
                        onClick={() => readAloudText(getMessageText(chatContent), `ai-${index}`)}
                      >
                        <FontAwesomeIcon
                          icon={speakingId === `ai-${index}` ? faStop : faHeadphones}
                          size="1x"
                          className="pe-1"
                        />
                      </button>
                      <button
                        type="button" 
                        className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                        data-bs-toggle="tooltip" 
                        data-bs-placement="top" 
                        title={t('chat:title.copy')}
                      >
                        <FontAwesomeIcon
                          icon={faCopy}
                          onClick={e => 
                            {
                              let content = chatContent.map(x => x.type === 'text' ? x.element : x.element.toString()).join('\n ');
                              navigator.clipboard.writeText(content);
                              let copiedToast = document.getElementById("CopiedTextSuccesfull");
                              if(!copiedToast) return;
                              copiedToast.style.display = 'block';
                              setTimeout(() => {
                                if(copiedToast){
                                  copiedToast.style.display = 'none';
                                }
                              }, 1000);
                            }
                          }
                          size="1x"
                          className="pe-1"
                        />
                      </button>
                    </div>
                  </div>
                );
              }
            })}
            {runtimeState.state === "thinking" && (
              streamingContent.length > 0
                ? <div className="message-left">
                    <ChatComponent items={streamingContent} id="streaming" />
                  </div>
                : <div className="message-left">
                    <div className="message-left-content">
                        <span className="spin me-2"> {stateIconWaiting} </span>
                        <span className="text-secondary"> {thinkingText} </span>
                    </div>
                  </div>
            )}
            {runtimeState.state === "error" && (
              <div className="message-left">
                <div className="message-left-content border border-danger border-opacity-50 rounded" role="alert" >
                    <div className="d-flex align-items-start gap-2">
                        <span className="text-danger"> {stateIconError} </span>
                        <div className="flex-grow-1">
                            <div className="fw-semibold"> {t("chat:error.title")} </div>
                            <div className="small text-secondary"> {runtimeState.error ?? t("chat:error.requestFailed")} </div>
                        </div>
                        <button
                            type="button"
                            className="btn border-0 bg-transparent btn-sm text-secondary flex-shrink-0"
                            onClick={() => retryLastUserMessage()}
                            title={t("chat:error.retry")}
                        >
                          <FontAwesomeIcon icon={faRotateLeft}/>
                      </button>
                    </div>
                </div>
              </div>
            )}
            {chatMessages.length > 0 && <AiDisclosureNotice />}
          </div>
        </div>
        <div id="section-chat-input" className="row" data-tour="chat-input">
          <div className="chat-input-grid">
            <div className="chat-options">
              {SHOW_ATTACHMENTS && (
              <div className="dropdown dropstart">
                <button 
                  className="btn btn-chat bg-transparent text-secondary border-0" 
                  type="button" 
                  id="dropdownFileUpload"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true" 
                  aria-expanded="false"
                >
                  <FontAwesomeIcon
                    icon={faPaperclip}
                    size="lg"
                    title={t('chat:title.attachFiles')}
                  />
                  <StageBadge feature="chatAttachments" className="ms-1 d-block mx-auto mt-1"/>
                </button>
                <ul className={`dropdown-menu ${effectiveTheme === 'dark' ? 'dropdown-menu-dark' : 'dropdown-menu-light'} ms-4 p-1 border rounded`} aria-labelledby="dropdownFileUpload" style={{textAlignLast:'start'}}>
                  <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`}>
                    <FontAwesomeIcon
                      icon= {faCamera}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadTokenImage">{t('chat:takePicture')} </label>
                    {/* <video id = 'fileuploadTokenImage' ref={videoRef} autoPlay style={{ maxWidth: '100%' }} />
                    <br />
                    <button onClick={handleCapture}>Bild aufnehmen</button>
                    <canvas ref={canvasRef} style={{ display: 'none' }} width={640} height={480} /> */}
                    <input
                      id = 'fileuploadTokenImage'
                      type= 'file'
                      accept='image/*'
                      capture="environment"
                      onChange={handleImageCapture}
                      hidden
                    />
                    {/* {takenPicture && (
                      <div>
                        <h3>Bildvorschau:</h3>
                        <img
                          src={takenPicture}
                          alt="Taken Picture"
                          style={{ maxWidth: '300px', marginTop: '20px' }}
                        />
                        <br />
                        <button onClick={uploadImage}>Upload</button>
                      </div>
                    )} */}
                  </li>
                  <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`} onClick={e => onClickButtonAttachFiles('fileuploadImage')}>
                    <FontAwesomeIcon
                      icon= { faImage}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadImage">{t('chat:attachImage')}</label>
                    <input
                        id = 'fileuploadImage'
                        type= 'file'
                        multiple
                        accept='image/png,image/jpg,image/jpeg'
                        onChange={e => {
                          addFiles(Array.from(e.target.files ?? []));
                          e.target.value = "";
                        }}
                        hidden
                    />
                  </li>
                  <li className={`dropdown-item ${effectiveTheme === 'dark' ? "text-light" : "text-dark"} p-1`} onClick={e => onClickButtonAttachFiles('fileuploadPDF')}>
                  <FontAwesomeIcon
                      icon= {faFolderOpen}
                      size="lg"
                      className="me-2"
                    />
                    <label htmlFor="fileuploadPDF">{t('chat:attachPdf')}</label>
                    <input
                        id = 'fileuploadPDF'
                        type= 'file'
                        multiple
                        accept='application/pdf'
                        onChange={e => {
                          addFiles(Array.from(e.target.files ?? []));
                          e.target.value = "";
                        }}
                        hidden
                    />
                  </li>
                  {pendingFiles.length > 0 && (
                    <>
                      <li> <hr className="dropdown-divider my-1" /> </li>
                      <li className={`dropdown-item ${effectiveTheme === "dark" ? "text-light" : "text-dark"} pt-0`}>
                          <a className="dropdown-item"
                              href="#"
                              onClick={e => e.preventDefault()}
                          >
                              <FontAwesomeIcon
                                  icon={faCaretLeft}
                                  size="xs"
                                  className="me-1"
                              />
                              {t("chat:deleteFile")}
                          </a>
                          <ul
                              className={`dropdown-menu dropdown-submenu ${
                                  effectiveTheme === "dark"
                                      ? "dropdown-menu-dark"
                                      : "dropdown-menu-light"
                              } ms-4 p-1 border rounded`}
                          >
                            {pendingFiles.map((attachment, index) => (
                                <li key={`${attachment.file.name}-${index}`}
                                    className={`dropdown-item ${effectiveTheme === "dark"? "text-light": "text-dark"} p-1`}
                                    onClick={() => deleteAttachedFile(index)}
                                >
                                    {attachment.file.name}
                                </li>
                              ))}
                            </ul>
                        </li>
                      </>
                    )}
                </ul>
              </div>
              )}
            </div>
            <div className="chat-inputs">
              <div className="row p-2">
                  <div className="col-12 text-secondary">
                    {pendingFiles.length > 0 && (
                      <div className="d-flex flex-nowrap gap-2 p-2 overflow-x-auto overflow-y-hidden w-100" style={{minWidth: 0, scrollbarWidth: "thin"}}>
                          {pendingFiles.map( (attachment, index) => {
                            const fileName = attachment.file.name;
                            const shortName = fileName.length > 15  ? `${fileName.slice(0, 15)}...` : fileName;
                            return (
                              <div
                                key={`${attachment.file.name}-${index}`}
                                className="border border-secondary rounded px-2 py-1 d-flex align-items-center gap-2 flex-shrink-0"
                              >
                                <span className="text-nowrap" style={{ maxWidth: "160px" }}> {shortName} </span>
                                <button
                                  type="button"
                                  className="btn border-0 p-0 text-secondary flex-shrink-0"
                                  onClick={() => deleteAttachedFile(index)}
                                  aria-label={`${t("chat:deleteFile")} ${fileName}`}
                                >
                                  ×
                                </button>
                              </div>
                            )
                          })}
                      </div>
                  )}
                  </div>
                </div>
                <div className=" row chat-input border p-0">
                  <div className="input-group p-0">
                      <textarea 
                        id="chat-input" 
                        className="form-control chat-input border-0" 
                        aria-labelledby="chat-input-label" 
                        onChange={onChatInputChangedHandler} 
                        onKeyDown={onChatInputKeyDownHandler} 
                        style={{resize:'none'}}
                        ref={chatInputRef}
                        value={chatInputString}>
                      </textarea>
                      <div className="d-grid" id="chat-input-label">
                        { recognition 
                          ? <button
                              type="button"
                              onClick={toggleRecognition}
                              className={`btn-chat bg-transparent border-0 p-0 pe-1 mt-1 ${recognizing ? "" : "text-secondary"}`}
                              data-bs-toggle="tooltip"
                              data-bs-placement="top"
                              title={recognizing ? t("chat:title.stopRecording") : t("chat:title.record")}
                              id="chat-input-label-audio"
                              style={{
                                alignSelf: "start",
                                color: recognizing
                                    ? effectiveTheme === "dark"
                                        ? SengiColors.sng_lavendel
                                        : SengiColors.sng_veilchen
                                    : undefined,
                                transition: "color 0.15s ease"
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faMicrophoneLines}
                                size="lg"
                              />
                            </button>
                          : <>
                            <button
                              disabled={true}
                              className="btn-chat bg-transparent text-secondary border-0 p-0 pe-1 mt-1"
                              data-bs-toggle="tooltip" 
                              data-bs-placement="top" 
                              title={t('chat:title.recordUnsupported')}
                              id="chat-input-label"
                              style={{alignSelf:'start'}}
                            >
                              <FontAwesomeIcon
                                icon={faMicrophoneLinesSlash}
                                size="lg"
                              />
                            </button>
                          </>
                        }
                        {/* <button
                          className="btn-chat bg-transparent text-secondary border-0 p-0 pe-1 mb-1"
                          data-bs-toggle="tooltip" 
                          data-bs-placement="top" 
                          title={t('chat:title.adjust')}
                          id="chat-input-label-tools"
                          style={{alignSelf:'start'}}
                          onClick={(e => {
                            setInputSectionSpacing(!showAccordion ? 310 : 170);
                            setShowAccordion(!showAccordion);
                          })}
                        >
                          <FontAwesomeIcon
                            icon={faScrewdriverWrench}
                            size="lg"
                          />
                        </button> */}
                      </div>
                  </div>
                </div>
                {speechError && (
                  <div className="small text-danger mt-1">
                      {t("chat:error.microphone")}
                  </div>
                )}
                {/* { showAccordion
                  ? <div className="mt-3">
                      <div className="h3">
                        <FontAwesomeIcon
                          icon={faGears}
                          size="1x"
                          className="me-2"
                        />
                        {t('chat:messageSettings')}
                      </div>
                      <div className="col-10 ms-5">
                        <div className='form-check'>
                          <input
                            className={`form-check-input ${effectiveTheme === 'dark' ? 'border-light': 'border-dark'}`}
                            type='checkbox'
                            name='secureGPT'
                            id='secureGPT'
                            checked={accodionInput.secureGPT}
                            onChange={event => handleChangeAccordionCheckboxInput(event)}
                          />
                          <label htmlFor='secureGPT' className='form-check-label'>
                            SecureGPT
                          </label>
                        </div>
                        <div className='mt-2 input-group'>
                          <label htmlFor='aiService' className={`input-group-text ${!foundService ? 'border-danger' : ''}`} >
                            {t('chat:aiService')}
                          </label>
                          <input 
                            className={`form-control rounded-end ${!foundService ? 'border-danger' : ''}`} 
                            list="datalistOptionsAiService" 
                            placeholder={t('chat:selectService')}
                            name="aiService"
                            value={accodionInput.aiService}
                            onChange={(event) => handleChangeAiServiceInput(event)}
                          />
                          <datalist id="datalistOptionsAiService">
                            {aiServiceList.map(item =>(
                              <option 
                                key={item} 
                                selected={item === accodionInput.aiService} 
                                value={item}
                                onClick={(e => {
                                  setAccordionInput(inputs =>({...inputs,['aiService']:item}));
                                  setFoundService(true);
                                })}
                              ></option>
                            ))}
                          </datalist>
                          
                        </div>
                        { !foundService
                          ? <div className='row'>
                              <p className='text-danger'>{t('chat:serviceNotFound')}</p>
                            </div>
                          : <></>

                        }
                      </div>
                    </div>
                  : <></> 
                } */}
              </div>
              <div className="chat-actions">
                {runtimeState.state === "thinking" ? (
                  // Waehrend eine Antwort laeuft wird Senden zum Stop (bricht den Stream ab, Paket 4b).
                  <button
                    type="button"
                    className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                    title={t('chat:title.stopResponse')}
                    onClick={() => abortChatRun(chatID)}
                  >
                    <FontAwesomeIcon icon={faStop}/>
                  </button>
                ) : (
                <div className="" onClick={onClickChatInputSendHandler}>
                  <button
                    className="btn-chat bg-transparent border border-secondary rounded-circle text-secondary"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={t('chat:title.send')}
                    disabled={chatInputString.trim() === '' && pendingFiles.length === 0}
                  >
                    <FontAwesomeIcon icon={faChevronRight}/>
                  </button>
                </div>
                )}
                {SHOW_COST_ESTIMATE && <span className="chat-input-estimated-coins" >{estimatedIsUpperBound ? t('chat:upToPrefix') + ' ' : ''}{estimatedCost ?? '—'} <img src={sengiCoin} alt='Sengi Coins' title="Sengi Coins" style={{height:"20px", marginLeft:"0px", marginRight:"0px", opacity: "100%",filter: "drop-shadow(0 2px 10px rgba(27,73,101, 1))" }}/></span>}
              </div>
            </div>
          </div>
      </div>
    </>
  );
}
